import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio
} from '@mui/material';
import { 
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import AdminButton from 'components/common/AdminButton';
import { format, parseISO } from 'date-fns';
import { updateEmployeeSchema } from '../../../schemas/employeeSchema';

/**
 * Overview tab for employee details - displays and allows editing of basic information
 */
function EmployeeOverviewTab({ 
  employee, 
  onUpdate, 
  onUnsavedChanges,
  loading = false 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [showTerminationOptions, setShowTerminationOptions] = useState(false);
  const [terminationDialogOpen, setTerminationDialogOpen] = useState(false);
  const [userAccessDecision, setUserAccessDecision] = useState('maintain');

  // Initialize form data when employee changes
  useEffect(() => {
    setFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phoneNumber: employee.phoneNumber || '',
      employmentType: employee.employmentType || '',
      hireDate: employee.hireDate || '',
      terminationDate: employee.terminationDate || ''
    });
    setIsEditing(false);
    setErrors({});
    setShowTerminationOptions(false);
  }, [employee]);

  // Notify parent of unsaved changes
  useEffect(() => {
    if (onUnsavedChanges) {
      if (isEditing) {
        const hasChanges = Object.keys(formData).some(
          key => formData[key] !== (employee[key] || '')
        );
        onUnsavedChanges(hasChanges);
      } else {
        // Not editing, so no unsaved changes
        onUnsavedChanges(false);
      }
    }
  }, [formData, isEditing, employee, onUnsavedChanges]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = async () => {
    try {
      // Validate using Yup schema
      await updateEmployeeSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      // Convert Yup validation errors to our error format
      const newErrors = {};
      if (validationError.inner) {
        validationError.inner.forEach(error => {
          newErrors[error.path] = error.message;
        });
      }
      setErrors(newErrors);
      return false;
    }
  };

  const handleSave = async () => {
    const isValid = await validateForm();
    if (!isValid) return;
    
    // Check if termination is being processed
    if (showTerminationOptions && formData.terminationDate) {
      // If employee has assignments or a linked user, show confirmation dialog
      const hasAssignments = employee.assignmentCount > 0 || (employee.assignments && employee.assignments.length > 0);
      const hasLinkedUser = !!employee.userId;
      
      if (hasAssignments || hasLinkedUser) {
        setTerminationDialogOpen(true);
        return;
      }
      
      // Otherwise proceed with termination
      await processTermination();
    } else {
      // Regular save without termination
      try {
        await onUpdate({
          id: employee.id,
          updates: formData
        });
        setIsEditing(false);
        setShowTerminationOptions(false);
        if (onUnsavedChanges) onUnsavedChanges(false);
      } catch (error) {
        console.error('Error updating employee:', error);
      }
    }
  };
  
  const processTermination = async () => {
    try {
      const updates = {
        ...formData,
        isActive: false,
        terminationDate: formData.terminationDate || new Date().toISOString().split('T')[0]
      };
      
      await onUpdate({
        id: employee.id,
        updates,
        closeAssignments: true,
        userAccessDecision: employee.userId ? userAccessDecision : null
      });
      
      setIsEditing(false);
      setShowTerminationOptions(false);
      setTerminationDialogOpen(false);
      if (onUnsavedChanges) onUnsavedChanges(false);
    } catch (error) {
      console.error('Error terminating employee:', error);
    }
  };
  
  const handleTerminationConfirm = () => {
    processTermination();
  };
  
  const handleTerminationCancel = () => {
    setTerminationDialogOpen(false);
    setShowTerminationOptions(false);
    setFormData(prev => ({ ...prev, terminationDate: '' }));
  };

  const handleCancel = () => {
    setFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phoneNumber: employee.phoneNumber || '',
      employmentType: employee.employmentType || '',
      hireDate: employee.hireDate || '',
      terminationDate: employee.terminationDate || ''
    });
    setErrors({});
    setIsEditing(false);
    setShowTerminationOptions(false);
    if (onUnsavedChanges) onUnsavedChanges(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const employmentTypeOptions = [
    { value: 'FULL_TIME', label: 'Full Time' },
    { value: 'PART_TIME', label: 'Part Time' },
    { value: 'CONTRACTOR', label: 'Contractor' },
    { value: 'TEMPORARY', label: 'Temporary' },
    { value: 'SEASONAL', label: 'Seasonal' }
  ];

  return (
    <Box>
      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        {!isEditing ? (
          <AdminButton
            startIcon={<EditIcon />}
            onClick={() => setIsEditing(true)}
            disabled={loading}
          >
            Edit
          </AdminButton>
        ) : (
          <Box display="flex" gap={1}>
            <AdminButton
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </AdminButton>
            <AdminButton
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
            >
              Save Changes
            </AdminButton>
          </Box>
        )}
      </Box>

      {/* Basic Information */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Basic Information
        </Typography>
        <Divider sx={{ my: 1 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                size="small"
                required
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    First Name
                  </Typography>
                  <Typography variant="body2">
                    {employee.firstName}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                size="small"
                required
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <PersonIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Name
                  </Typography>
                  <Typography variant="body2">
                    {employee.lastName}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">
                    {employee.email || '-'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber || 'Format: (555) 123-4567 or +1 555-123-4567'}
                size="small"
                placeholder="(555) 123-4567"
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {employee.phoneNumber || '-'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Employment Information */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Employment Information
        </Typography>
        <Divider sx={{ my: 1 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <TextField
                fullWidth
                label="Hire Date"
                type="date"
                value={formData.hireDate ? formData.hireDate.split('T')[0] : ''}
                onChange={(e) => handleInputChange('hireDate', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Hire Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(employee.hireDate)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <Box>
                <TextField
                  fullWidth
                  label="Termination Date"
                  type="date"
                  value={formData.terminationDate ? formData.terminationDate.split('T')[0] : ''}
                  onChange={(e) => handleInputChange('terminationDate', e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled={!showTerminationOptions}
                />
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <CalendarIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Termination Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(employee.terminationDate)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            {isEditing ? (
              <FormControl fullWidth size="small">
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.employmentType}
                  onChange={(e) => handleInputChange('employmentType', e.target.value)}
                  label="Employment Type"
                  sx={{ height: 40 }}
                >
                  <MenuItem value="">None</MenuItem>
                  {employmentTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <WorkIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Employment Type
                  </Typography>
                  <Typography variant="body2">
                    {employmentTypeOptions.find(opt => opt.value === employee.employmentType)?.label || '-'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
          
          {isEditing && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showTerminationOptions}
                    onChange={(e) => {
                      setShowTerminationOptions(e.target.checked);
                      if (!e.target.checked) {
                        handleInputChange('terminationDate', '');
                      } else {
                        // Set default to today
                        handleInputChange('terminationDate', new Date().toISOString().split('T')[0]);
                      }
                    }}
                    disabled={employee.isActive === false}
                  />
                }
                label="Terminate Employee"
              />
              {showTerminationOptions && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Terminating an employee will:
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Close all active assignments</li>
                    <li>Set the employee status to Inactive</li>
                    {employee.userId && <li>Require a decision about RadixDash user access</li>}
                  </ul>
                </Alert>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Additional Information */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Additional Information
        </Typography>
        <Divider sx={{ my: 1 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">
                {formatDate(employee.createdAt)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {formatDate(employee.updatedAt)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Termination Confirmation Dialog */}
      <Dialog
        open={terminationDialogOpen}
        onClose={() => setTerminationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Employee Termination</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            You are about to terminate <strong>{employee.firstName} {employee.lastName}</strong>.
          </Typography>
          
          {(employee.assignmentCount > 0 || (employee.assignments && employee.assignments.length > 0)) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This employee has {employee.assignmentCount || employee.assignments?.length} active assignment(s) that will be closed.
            </Alert>
          )}
          
          {employee.userId && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                This employee has RadixDash user access. What would you like to do?
              </Typography>
              <RadioGroup
                value={userAccessDecision}
                onChange={(e) => setUserAccessDecision(e.target.value)}
              >
                <FormControlLabel
                  value="maintain"
                  control={<Radio />}
                  label="Maintain user access (person can still login to RadixDash)"
                />
                <FormControlLabel
                  value="revoke"
                  control={<Radio />}
                  label="Revoke user access (terminate RadixDash access)"
                />
              </RadioGroup>
            </Box>
          )}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The employee can be reactivated later if needed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <AdminButton onClick={() => handleTerminationCancel()}>
            Cancel
          </AdminButton>
          <AdminButton
            variant="contained"
            color="error"
            onClick={handleTerminationConfirm}
          >
            Confirm Termination
          </AdminButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

EmployeeOverviewTab.propTypes = {
  employee: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onUnsavedChanges: PropTypes.func,
  loading: PropTypes.bool
};

export default EmployeeOverviewTab;